import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { consultationsApi } from './patientsClient';
import type { StartConsultationInput } from '@/types/patients';

export const CONSULTATIONS_KEY = ['consultations'] as const;

const byPatientKey = (patientId: string | null | undefined) =>
  [...CONSULTATIONS_KEY, 'byPatient', patientId] as const;
const detailKey = (id: string | null | undefined) =>
  [...CONSULTATIONS_KEY, 'detail', id] as const;

export function usePatientConsultations(
  patientId: string | null | undefined,
  params: { limit?: number; offset?: number; enabled?: boolean } = {},
) {
  const { limit, offset, enabled = true } = params;
  const active = enabled && !!patientId;
  return useQuery({
    queryKey: [...byPatientKey(patientId), { limit, offset }],
    queryFn: () => consultationsApi.list(patientId as string, { limit, offset }),
    enabled: active,
    staleTime: 15_000,
  });
}

export function useConsultation(id: string | null | undefined, opts: { enabled?: boolean } = {}) {
  const enabled = (opts.enabled ?? true) && !!id;
  return useQuery({
    queryKey: detailKey(id),
    queryFn: () => consultationsApi.get(id as string),
    enabled,
    staleTime: 15_000,
  });
}

export function useStartDraft(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartConsultationInput = {}) =>
      consultationsApi.startDraft(patientId, input),
    onSuccess: (consultation) => {
      qc.invalidateQueries({ queryKey: byPatientKey(patientId) });
      qc.invalidateQueries({ queryKey: detailKey(consultation.id) });
    },
  });
}

// Autosave + finalize are NOT exposed as standalone mutation hooks: the consult
// form's `useConsultationDraft` engine owns that orchestration (CAS-token
// threading, debounced two-resource saves, conflict handling, flush-before-
// finalize) and calls `consultationsApi.autosave/finalize` directly. A separate
// mutation hook here would be a second, divergent source of truth — avoided.
